import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEmailsTransmissionsV1EmailTransmissionServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/email-transmissions/v1',
        destPath: '/v1',
      },
      {
        srcPath: '/emails/transmissions/v1/email-transmissions',
        destPath: '/v1/email-transmissions',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/email-transmissions/v1',
        destPath: '/v1',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/emails/transmissions/v1/email-transmissions',
        destPath: '/v1/email-transmissions',
      },
    ],
    _: [
      {
        srcPath: '/emails/transmissions/v1/email-transmissions',
        destPath: '/v1/email-transmissions',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/transmissions',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/backoffice/v1/email-transmissions',
        destPath: '/v1/backoffice/email-transmissions',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/backoffice/v1/email-transmissions',
        destPath: '/v1/backoffice/email-transmissions',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/backoffice/v1/email-transmissions',
        destPath: '/v1/backoffice/email-transmissions',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_email-transmissions_email-transmissions';

/**
 * Creates and sends an email transmission to one or more recipients.
 *
 * The email is sent asynchronously. The returned transmission has `ACCEPTED` status.
 * Call [Get Email Transmission](https://dev.wix.com/docs/api-reference/business-management/marketing/emails/email-transmission-v1/get-email-transmission) or listen for the `EmailTransmissionCreated` webhook to track delivery status.
 */
export function sendEmailTransmission(
  payload: object
): RequestOptionsFactory<any> {
  function __sendEmailTransmission({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'emailTransmission.createdDate' },
          { path: 'emailTransmission.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.emails.transmissions.v1.email_transmission',
      method: 'POST' as any,
      methodFqn:
        'wix.emails.transmissions.v1.EmailTransmissionService.SendEmailTransmission',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEmailsTransmissionsV1EmailTransmissionServiceUrl({
        protoPath: '/v1/email-transmissions/send',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'emailTransmission.createdDate' },
              { path: 'emailTransmission.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __sendEmailTransmission;
}

/** Retrieves an email transmission by ID. */
export function getEmailTransmission(
  payload: object
): RequestOptionsFactory<any> {
  function __getEmailTransmission({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.emails.transmissions.v1.email_transmission',
      method: 'GET' as any,
      methodFqn:
        'wix.emails.transmissions.v1.EmailTransmissionService.GetEmailTransmission',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEmailsTransmissionsV1EmailTransmissionServiceUrl({
        protoPath: '/v1/email-transmissions/{emailTransmissionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'emailTransmission.createdDate' },
              { path: 'emailTransmission.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getEmailTransmission;
}

/**
 * Retrieves a list of up to 100 email transmissions, given the provided paging, filtering, and sorting.
 *
 * `emailHtmlContent` is not returned in the results. To retrieve the full HTML content of a specific transmission,
 * call [Get Email Transmission](https://dev.wix.com/docs/api-reference/business-management/marketing/emails/email-transmission-v1/get-email-transmission).
 *
 * Query Email Transmissions runs with these defaults, which you can override:
 *
 * - `updatedDate` is sorted in `DESC` order
 * - `paging.limit` is `50`
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging),
 * and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 */
export function queryEmailTransmissions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryEmailTransmissions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.emails.transmissions.v1.email_transmission',
      method: 'POST' as any,
      methodFqn:
        'wix.emails.transmissions.v1.EmailTransmissionService.QueryEmailTransmissions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEmailsTransmissionsV1EmailTransmissionServiceUrl({
        protoPath: '/v1/email-transmissions/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'emailTransmissions.createdDate' },
              { path: 'emailTransmissions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryEmailTransmissions;
}

/**
 * Deletes an email transmission by ID.
 *
 * Deletion is only allowed for transmissions with `PROCESSED` or `REJECTED` status.
 */
export function deleteEmailTransmission(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteEmailTransmission({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.emails.transmissions.v1.email_transmission',
      method: 'DELETE' as any,
      methodFqn:
        'wix.emails.transmissions.v1.EmailTransmissionService.DeleteEmailTransmission',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEmailsTransmissionsV1EmailTransmissionServiceUrl({
        protoPath: '/v1/email-transmissions/{emailTransmissionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteEmailTransmission;
}
